import { FC, ReactElement, useCallback, useEffect } from "react";
import { NetworkPortfolioManagementProject, SortByOption } from "@app/network-portfolio-management/interfaces";
import { useInfiniteScroll } from "@app/shared/useInfiniteScroll";
import { ProjectCard } from "@app/network-portfolio-management/components/ProjectCard";
import { ProjectCardSkeleton } from "@app/network-portfolio-management/components/ProjectCardSkeleton";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { PAGE_SIZE, useProjectInfiniteQuery } from "@app/network-portfolio-management/hooks/useProjectInfiniteQuery";
import { useGapByCountyStatusByPage } from "@app/network-portfolio-management/hooks/useGapByCountyStatusByPage";

export { PAGE_SIZE };

// Small bottom-loading indicator shown while a subsequent page is in flight (not a full-page placeholder).
const SCROLL_SKELETON_COUNT = 3;

export const renderSkeletons = (count: number): ReactElement[] =>
  Array.from({ length: count }, (_, i) => <ProjectCardSkeleton key={i} />);

interface ProjectInfiniteListProps {
  projectIds: number[];
  /**
   * Sort order for this list. Changing this resets pagination to page 1 in the new global order.
   */
  sortBy: SortByOption;
  selectedProjectIds: Set<number>;
  onToggleSelect: (id: number) => void;
  /** When false the per-project selection checkbox is hidden (no Project Action filter active). */
  selectionEnabled: boolean;
  /** Reports how many rows have loaded so an ancestor can show a "shown" count. */
  onLoadedCountChange?: (loaded: number) => void;
  /** Reports the loaded rows so an ancestor (e.g. the publish modal) can resolve selected names. */
  onLoadedRowsChange?: (rows: NetworkPortfolioManagementProject[]) => void;
  /**
   * When true, `projectIds` is already globally sorted (e.g. from the project-resources/sorted
   * or project-resources/grouped endpoints). The component slices the array client-side so that
   * each getProjectsData call enriches only the current page's 25 IDs instead of the full set.
   */
  isSorted?: boolean;
}

/**
 * Self-contained, infinitely-scrolling card list for a single id set. Used for both the flat
 * (ungrouped) list and each Group By section (which renders collapsed by default, so its scroll
 * sentinel only becomes reachable once the user expands the group). Rows mode is handled entirely
 * by ProjectTable now — this component is cards-only.
 */
export const ProjectInfiniteList: FC<ProjectInfiniteListProps> = ({
  projectIds,
  sortBy,
  selectedProjectIds,
  onToggleSelect,
  selectionEnabled,
  onLoadedCountChange,
  onLoadedRowsChange,
  isSorted = false,
}) => {
  const totalCount = projectIds.length;

  const { rows, pages, error, fetchNextPage, hasLoadedAny, hasNextPage, isFetchingNextPage, isLoading } = useProjectInfiniteQuery({
    isSorted,
    projectIds,
    sortBy,
  });

  // isSorted's client-side slicing means pages here already line up with the same page boundaries
  // getProjectsData used, so a page's gap-by-status result always matches the cards it was fetched for.
  const { gapByStatusByProjectId, gapByStatusQueries, pageIndexByProjectId } = useGapByCountyStatusByPage(pages);

  useEffect(() => {
    onLoadedCountChange?.(rows.length);
  }, [rows.length, onLoadedCountChange]);

  useEffect(() => {
    onLoadedRowsChange?.(rows);
  }, [rows, onLoadedRowsChange]);

  const isAbortError = (error as DOMException)?.name === "AbortError";
  const initialLoadError = error && !isAbortError && !hasLoadedAny ? "Failed to load Project. Please try again." : null;
  const scrollLoadError = error && !isAbortError && hasLoadedAny ? "Failed to load Additional Projects. Please try again." : null;

  const loadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { sentinelRef } = useInfiniteScroll(loadMore, hasNextPage);

  if (isLoading) {
    const count = Math.min(PAGE_SIZE, totalCount);
    return <div className="network-portfolio-management-project-cards">{renderSkeletons(count)}</div>;
  }

  if (initialLoadError) {
    return (
      <QESAlert type="danger" persist={true}>
        {initialLoadError}
      </QESAlert>
    );
  }

  if (rows.length === 0) {
    return <h3 className="text-muted no-content-text">No Project Data to Display</h3>;
  }

  return (
    <>
      <ul className="network-portfolio-management-project-cards" data-testid="network-portfolio-management-project-cards">
        {rows.map((row) => {
          const pageIndex = pageIndexByProjectId.get(row.projectId);
          const pageQuery = pageIndex != null ? gapByStatusQueries[pageIndex] : undefined;
          return (
            <li key={row.projectId}>
              <ProjectCard
                project={row}
                isSelected={selectedProjectIds.has(row.projectId)}
                onToggleSelect={onToggleSelect}
                selectionEnabled={selectionEnabled}
                gapByCountyStatus={gapByStatusByProjectId.get(row.projectId)}
                isGapByCountyStatusLoading={!!pageQuery?.isLoading}
                gapByCountyStatusHasError={!!pageQuery?.error}
              />
            </li>
          );
        })}
      </ul>

      {/* The infinite-scroll sentinel; auto-loads the next page when it scrolls into view. */}
      <div ref={sentinelRef} style={{ height: 1 }} />
      {scrollLoadError && (
        <QESAlert type="warning" persist={true}>
          {scrollLoadError}
        </QESAlert>
      )}
      {isFetchingNextPage && !scrollLoadError && (
        <div className="network-portfolio-management-project-cards">{renderSkeletons(SCROLL_SKELETON_COUNT)}</div>
      )}
      {!hasNextPage && !isFetchingNextPage && (
        <p className="network-portfolio-management-project-list__end-of-list" data-testid="network-portfolio-management-end-of-list">
          All Projects Loaded
        </p>
      )}
    </>
  );
};
